import axios from 'axios';
import React, { useEffect, useState, useContext, useRef } from 'react';
import Drawer from "../../components/UI/Drawer";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import DeleteButton from "../../components/UI/AlertDialog";
import DropdownMenu from "../../components/UI/DropdownMenu";
import { MoreVertical } from 'lucide-react';
import { getToken } from '../../utils/tokenUtils';
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";

// Actions dropdown used in the table rows
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, onView, onEdit, onDelete, activeTab }) => {
  const buttonRef = useRef(null);

  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
          <button
            onClick={() => { onView(row); setOpenMenu(null); }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            View
          </button>
          <button
            onClick={() => { onEdit(row); setOpenMenu(null); }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={() => {
              // // console.log('Delete clicked', { rowId: row.id, attendanceId: row?.attendance?.id, activeTab });
              (onDelete?.length === 0 ? onDelete() : onDelete?.(row.id));
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const CheckInApprovalPage = () => {
  const token = getToken();
  const [activeTab, setActiveTab] = useState("checkIn"); // "checkIn" or "checkOut"
  const [checkInData, setCheckInData] = useState([]);
  const [checkOutData, setCheckOutData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [editAttendanceData, setEditAttendanceData] = useState({
    check_in_time: "",
    check_out_time: "",
  });
  const [openMenu, setOpenMenu] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { roles } = useContext(AuthContext);
  const getRoleBasedRouteLocal = (route) => getRoleBasedRoute(route, roles);

  const fetchData = async (type, page = 1) => {
    setLoading(true);
    try {
      const endpoint =
        type === "checkIn"
          ? `https://hrmsnew.prepstripe.com/api/pending-checkin?page=${page}`
          : `https://hrmsnew.prepstripe.com/api/pending-checkout?page=${page}`;
      const res = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (type === "checkIn") {
        setCheckInData(res.data.data.data);
      } else {
        setCheckOutData(res.data.data.data);
      }
      setPagination({
        currentPage: res.data?.data.current_page || 1,
        totalPages: res.data?.data.last_page || 1,
        totalItems: res.data?.data.total || 0,
        perPage: res.data?.data.per_page || 15,
      });
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab, currentPage);
  }, [activeTab, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchData(activeTab, newPage);
    }
  };

  const handleEditClick = (record) => {
    setSelectedRequest(record);
    const timeKey = activeTab === "checkIn" ? "check_in_time" : "check_out_time";
    const sourceValue = record?.attendance?.[timeKey] || "";
    setEditAttendanceData((prev) => ({
      ...prev,
      [timeKey]: toDateTimeLocalValue(sourceValue),
    }));
    setEditModalOpen(true);
  };


  // update particular attendance
  const handleUpdateAttendance = async (event) => {
      event.preventDefault();

      try {
          const timeKey = activeTab === "checkIn" ? "check_in_time" : "check_out_time";
          const payload = { [timeKey]: editAttendanceData[timeKey] };
          const attendanceId = selectedRequest?.attendance_id || selectedRequest?.attendance?.id || selectedRequest?.id;
          const response = await axios.put(
          `https://hrmsnew.prepstripe.com/api/attendance/${attendanceId}`,
          payload,
          {
              headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              },
          }
          );

          setNotification(response?.data?.message || "Attendance updated successfully.");
          setEditModalOpen(false);
          fetchData(activeTab, currentPage);
      } catch (error) {
          console.error(error);
          if (error.response) {
          setNotification(error.response.data.message || "Update failed");
          } else {
          setNotification("Something went wrong!");
          }
      }
  };

  // Handle Approve/Reject
  const handleApproval = async () => {
      if (!selectedRequest || !selectedStatus) return;

      setIsSubmitting(true);

      try {
          const reviewPath = activeTab === "checkIn" ? "review-checkin" : "review-checkout";
          const attendanceId = selectedRequest?.attendance_id || selectedRequest?.attendance?.id || selectedRequest?.id;
          const res = await axios.post(
          `https://hrmsnew.prepstripe.com/api/attendance/${attendanceId}/${reviewPath}`,
          {
              approval_status: selectedStatus,
              manager_remark: approvalComment,
          },
          {
              headers: {
              Authorization: `Bearer ${token}`,
              },
          }
          );

          fetchData(activeTab, currentPage); // Refresh list
          closeApprovalModal();
          setIsDrawerOpen(false);
          setNotification(res?.data?.message || "Request status updated successfully");
      } catch (err) {
          console.error("Error submitting approval:", err);
          const errorMsg = err.response?.data?.message || err.response?.data?.error || "Something went wrong";
          setNotification(errorMsg);
      } finally {
          setIsSubmitting(false);
          // setIsDrawerOpen(false);
      }
  };

  
  // delete particular attendance
  const deleteAttendance = async (attendanceId) => {
      if (!attendanceId) return;

      // // console.log('deleteAttendance called', { attendanceId, activeTab, currentPage });

      try {
          const response = await fetch(`https://hrmsnew.prepstripe.com/api/attendance/${attendanceId}`, {
              method: 'DELETE',
              headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                  // Include authentication headers if needed
              },
              // credentials: 'include', // if using cookies for auth
          });

          const result = await response.json();

          if (response.ok) {
              setNotification(result.message); // or use toast
              fetchData(activeTab, currentPage);
          } else {
              setNotification(`Error: ${result.error || 'Failed to delete attendance'}`);
          }
      } catch (error) {
          console.error('Delete failed:', error);
          setNotification('An error occurred while deleting the attendance record.');
      }
  };

  const openDrawerForDetails = (request) => {
    setSelectedRequest(request);
    setIsDrawerOpen(true);
  };

  const closeApprovalModal = () => {
      setIsModalOpen(false);
  };

  const openApprovalModal = (request, status) => {
      setSelectedRequest(request);
      setSelectedStatus(status);
      setApprovalComment(""); // Clear previous comment
      setIsModalOpen(true);
  };
      
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRequest(null);
  };

  const formatTo12Hour = (timeStr) => {
    if (!timeStr) return "-";
    const date = new Date(timeStr);
    if (isNaN(date)) return "-";

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${hours}:${minutes} ${ampm}`;
  };
  const toDateTimeLocalValue = (dateTimeString) => {
    if (!dateTimeString) return "";
    const normalized = typeof dateTimeString === "string" ? dateTimeString.replace(" ", "T") : dateTimeString;
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return "";
    const tzOffsetMs = date.getTimezoneOffset() * 60000;
    const local = new Date(date.getTime() - tzOffsetMs);
    return local.toISOString().slice(0, 16);
  };

  const renderTable = (data) => (
    <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
      <table className="min-w-full table-auto md:table-fixed text-sm">
        <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
          <tr>
            <th className="py-2 px-4 text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Employee ID</th>
            <th className="py-2 px-4 text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">
              {activeTab === "checkIn" ? "Check-In" : "Check-Out"}
            </th>
            <th className="py-2 px-4 text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Remark</th>
            <th className="py-2 px-4 text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Status</th>
            <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" className="text-center py-4">
                Loading...
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center py-4">
                No Data Found
              </td>
            </tr>
          ) : (
            data.map((entry) => (
              <tr key={entry.id} className="border-b dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white hover:bg-gray-100">
                <td className="py-2 px-4">{entry.attendance.employee_id}</td>
                <td className="py-2 px-4">
                  {formatTo12Hour(entry.attendance[activeTab === "checkIn" ? "check_in_time" : "check_out_time"])}
                </td>
                <td className="py-2 px-4">{entry.manager_remark || "-"}</td>
                <td className="py-2 px-4 capitalize">{entry.approval_status || "pending"}</td>
                <td className="py-2 px-4 text-center">
                  <ActionsColumn
                    row={entry}
                    openMenu={openMenu}
                    setOpenMenu={setOpenMenu}
                    onView={openDrawerForDetails}
                    onEdit={handleEditClick}
                    onDelete={() => deleteAttendance(entry.id)}
                    activeTab={activeTab}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
        {/* Notification */}
        {notification && <Notification message={notification} onClose={() => setNotification(null)} />}

        {/* Breadcrumb */}
        <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
                <li>
                    <div className="flex items-center">
                        <Link
                        to={getRoleBasedRouteLocal("/admin/attendance_management")}
                        className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500"
                        >
                        Attendance Management
                        </Link>
                    </div>
                </li>
                <li aria-current="page">
                    <div className="flex items-center">
                        <svg className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
                        </svg>
                        <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">Check-In/Out Requests</span>
                    </div>
                </li>
            </ol>
        </nav>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">Check-in/out Requests</h2>
        </div>
        {/* Tabs */}
        <div className="flex gap-4 mb-4">
            <button
            onClick={() => setActiveTab("checkIn")}
            className={`px-4 py-2 rounded-md ${activeTab === "checkIn" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"}`}
            >
            Check-In Requests
            </button>
            <button
            onClick={() => setActiveTab("checkOut")}
            className={`px-4 py-2 rounded-md ${activeTab === "checkOut" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"}`}
            >
            Check-Out Requests
            </button>
        </div>

        {/* Table */}
        {renderTable(activeTab === "checkIn" ? checkInData : checkOutData)}

        {/* Pagination */}
        {!loading && (
            <ReactPaginate
            breakLabel="..."
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            pageCount={pagination.totalPages}
            renderOnZeroPageCount={null}
            nextLabel="Next >"
            previousLabel="< Prev"
            previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
            nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
            containerClassName="flex items-center justify-center mt-4"
            pageClassName="mx-1 px-3 py-1 border rounded"
            activeClassName="bg-blue-500 text-white"
            />
        )}

        {/* Drawer */}
        <Drawer border_radius='rounded-2xl' isOpen={isDrawerOpen} onClose={closeDrawer} showClose={false} title={`${activeTab === "checkIn" ? "Check-In" : "Check-Out"} Details`}>
 
            {selectedRequest && (
                <div className="p-2">
                  <div className='flex justify-between items-center'>
                    <h3 className="text-2xl font-semibold mb-4">Check-In Request</h3>
                    <button
                        onClick={closeDrawer}
                        className=" dark:hover:text-gray-400  text-xl px-2 py-1 rounded-md transition delay-100">
                        ✖
                    </button>
                  </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <div className="font-medium text-gray-600">Employee Name:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.user?.name || "N/A"}</div>
                    </div>

                    <div>
                        <div className="font-medium text-gray-600">Employee ID:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.employee_id || "N/A"}</div>
                    </div>

                    <div>
                        <div className="font-medium text-gray-600">Check-In Time:</div>
                            <div className="text-gray-800">
                                {/* {selectedRequest.attendance?.check_in_time || "N/A"} */}
                                {selectedRequest?.attendance?.check_in_time ? (() => {
                                    const date = new Date(selectedRequest.attendance.check_in_time);
                                    if (isNaN(date)) return '-';

                                    const options = { year: 'numeric', month: 'short', day: 'numeric' };
                                    const formattedDate = date.toLocaleDateString(undefined, options); // e.g. "Jul 22, 2024"

                                    let hours = date.getHours();
                                    const minutes = String(date.getMinutes()).padStart(2, '0');
                                    const ampm = hours >= 12 ? 'PM' : 'AM';
                                    hours = hours % 12 || 12;

                                    return `${formattedDate} ${hours}:${minutes} ${ampm}`;
                                })() : '-'}
                            </div>
                        </div>
                    <div>

                    <div className="font-medium text-gray-600">Check-In Location:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.check_in_location || "N/A"}</div>
                    </div>

                    <div>
                        <div className="font-medium text-gray-600">IP Address:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.ip_address || "N/A"}</div>
                    </div>

                    <div>
                        <div className="font-medium text-gray-600">Device Info:</div>
                        <div className="text-gray-800 break-words">{selectedRequest.attendance?.device_info || "N/A"}</div>
                    </div>

                    <div>
                        <div className="font-medium text-gray-600">Approval Status:</div>
                        <div className="capitalize text-yellow-600">{selectedRequest.approval_status || "pending"}</div>
                    </div>
                </div>

                {selectedRequest.attendance?.check_in_image && (
                    <div className="mt-6">
                    <div className="font-medium text-gray-600 mb-2">Check-In Image:</div>
                    <img
                        src={`https://hrmsnew.prepstripe.com/storage/attendance_images/${selectedRequest.attendance.check_in_image}`}
                        alt="Check-In"
                        className="rounded-md border w-48"
                    />
                    </div>
                )}

                  <div className="flex justify-end items-center mt-6 space-x-4">
                      <button
                      onClick={() => openApprovalModal(selectedRequest, "approved")}
                      // onClick={() => handleApproveReject(selectedRequest.id, "approved")}
                      className="w-44 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                      >
                      Approve
                      </button>
                      <button
                      onClick={() => openApprovalModal(selectedRequest, "rejected")}
                      className="w-44 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                      >
                      Reject
                      </button>
                  </div>
                </div>
            )}
        </Drawer>

        {/* edit checkin check out modal */}
        {editModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
            <div className="bg-white font-poppins rounded-t-2xl md:rounded-lg shadow-xl w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp">
                {/* <h2 className="text-lg font-semibold mb-4">Edit Attendance</h2> */}

                <form onSubmit={handleUpdateAttendance} className="bg-white sm:rounded-lg rounded-t-2xl"> 
                    
                    {/* modal title */}
                    <div className="px-4 py-3 flex border-b border-gray-300">
                        <h2 className="md:text-xl text-md font-semibold">
                            Edit Attendance
                        </h2>
                    </div>
                    
                    <div className="max-h-[80vh] overflow-y-auto flex flex-col gap-y-2 rounded-lg bg-white p-4">
                    {/* Check In/Out Time */}
                        <div>
                            <label className="block mb-1 font-medium">{activeTab === "checkIn" ? "Check In Time" : "Check Out Time"}</label>
                            <input
                                type="datetime-local"
                                value={editAttendanceData[activeTab === "checkIn" ? "check_in_time" : "check_out_time"] || ""}
                                onChange={(e) =>
                                  setEditAttendanceData((prev) => ({
                                    ...prev,
                                    [activeTab === "checkIn" ? "check_in_time" : "check_out_time"]: e.target.value,
                                  }))
                                }
                                required
                                className="w-full border p-2 rounded"
                                />

                        </div>
                    </div>

                    <div className="px-4 py-5 flex justify-between items-center">
                        <button
                            onClick={() => setEditModalOpen(false)}
                            className="px-4 py-2 bg-gray-400 text-white rounded"
                            >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded"
                            >
                            Update
                        </button>
                    </div>
                </form>
            </div>
        </div>
        )}

        {/* Modal for Approval with remark */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
              <div className="bg-white font-poppins rounded-t-2xl md:rounded-lg shadow-xl w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp">

                  {/* modal title */}
                  <div className="px-4 py-3 flex border-b border-gray-300">
                      <h2 className="md:text-xl text-md font-semibold">
                          You are {selectedStatus} the request:{" "}
                          <span className="font-bold">{selectedRequest?.full_name}</span>
                      </h2>
                  </div>

                  {/* modal body */}
                  <div className="max-h-[80vh] overflow-y-auto flex flex-col gap-y-2 rounded-lg bg-white p-4">

                      <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                          Remark
                          </label>
                          <textarea
                          className="w-full border border-gray-300 rounded-md px-3 py-2 h-24"
                          value={approvalComment}
                          onChange={(e) => setApprovalComment(e.target.value)}
                          placeholder="Add any comments..."
                          />
                      </div>
                  </div>

                  <hr className="text-gray-300"/>

                  <div className="px-4 py-5 flex justify-between items-center">
                      <button
                      onClick={closeApprovalModal}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-10 py-2 rounded-md"
                      >
                      Cancel
                      </button>
                      <button
                      onClick={handleApproval}
                      className={`px-4 py-2 rounded-md text-white ${
                          selectedStatus === "approved"
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                      disabled={isSubmitting}
                      >
                      {isSubmitting ? "Processing..." : selectedStatus}
                      </button>
                  </div>
              </div>
          </div>
        )}
      
    </div>
  );
};

export default CheckInApprovalPage;